// src/features/dashboard/components/TimeAllocationChart.tsx

import { useState } from 'react';

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';

import {
  useTimeAllocationChart,
} from '../hooks/useTimeAllocationChart';

import type {
  CustomTooltipProps,
} from '../types/timeAllocationChart.types';

import {
  renderLabel,
  renderActiveShape,
} from '../utils/timeAllocationChart.utils';

type TimeAllocationChartProps = {
  ManagerEmployeeCode: string;
};

const CustomTooltip = ({
  active,
  payload,
}: CustomTooltipProps) => {
  if (
    active &&
    payload &&
    payload.length
  ) {
    const current = payload[0];

    return (
      <div className="min-w-[190px] rounded-[10px] border border-[#E5E7EB] bg-white shadow-[0_6px_16px_rgba(0,0,0,0.12)]">
        <div className="border-b border-[#EEF2F7] px-3 py-2 text-[12px] font-semibold text-[#667085]">
          {current.name}
        </div>

        <div className="space-y-2 px-3 py-3">
          <div className="flex items-center gap-2">
            <div
              className="h-[11px] w-[11px] rounded-full"
              style={{
                backgroundColor:
                  current.payload.color,
              }}
            />

            <span className="text-[12px] text-[#4B5563]">
              Allocation:
            </span>

            <span className="ml-auto text-[12px] font-bold text-[#111827]">
              {current.value}%
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] text-[#667085]">
            <span>Total Hours</span>

            <span className="font-semibold">
              {current.payload.totalHours}h
            </span>
          </div>

          <div className="flex items-center justify-between text-[11px] text-[#667085]">
            <span>Associates</span>

            <span className="font-semibold">
              {current.payload.totalAssociates}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

const TimeAllocationChart = ({
  ManagerEmployeeCode,
}: TimeAllocationChartProps) => {

  const [
    activeIndex,
    setActiveIndex,
  ] = useState<number | null>(
    null,
  );

  const {
    chartData,
    loading,
    error,
  } = useTimeAllocationChart(
    ManagerEmployeeCode,
  );

  if (loading) {
    return (
      <div className="flex h-[320px] items-center justify-center rounded-[18px] border border-[#E5E7EB] bg-white text-[14px] text-[#667085]">
        Loading chart...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[320px] flex-col items-center justify-center rounded-[18px] border border-[#FECACA] bg-[#FEF2F2] text-center">
        <h3 className="text-[14px] font-semibold text-[#DC2626]">
          Time Allocation Chart Error
        </h3>

        <p className="mt-1 text-[12px] text-[#7F1D1D]">
          {error}
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col justify-start bg-white">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="text-[14px] font-bold text-[#002B5B]">
          Span Time Allocation
        </h2>

        <div className="whitespace-nowrap rounded-full bg-[#EDF4FF] px-3 py-[5px] text-[10px] font-medium text-[#2563EB]">
          {chartData[0]
            ?.totalAssociates ?? 0}{' '}
          associates
        </div>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="h-[210px] w-[210px]">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <PieChart>
              <Pie
                {...({
                  activeIndex:
                    activeIndex ?? -1,
                  activeShape:
                    renderActiveShape,
                } as any)}
                data={chartData}
                dataKey="value"
                innerRadius={52}
                outerRadius={90}
                paddingAngle={1}
                label={renderLabel}
                labelLine={false}
                onMouseEnter={(
                  _: any,
                  index: number,
                ) =>
                  setActiveIndex(
                    index,
                  )
                }
                onMouseLeave={() =>
                  setActiveIndex(
                    null,
                  )
                }
              >
                {chartData.map(
                  (
                    item,
                    index,
                  ) => (
                    <Cell
                      key={index}
                      fill={
                        activeIndex ===
                          null ||
                        activeIndex ===
                          index
                          ? item.color
                          : '#D1D5DB'
                      }
                    />
                  ),
                )}
              </Pie>

              <Tooltip
                content={
                  <CustomTooltip />
                }
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 px-2">
          {chartData.map(
            (
              item,
              index,
            ) => (
              <div
                key={item.name}
                onMouseEnter={() =>
                  setActiveIndex(
                    index,
                  )
                }
                onMouseLeave={() =>
                  setActiveIndex(
                    null,
                  )
                }
                className="flex cursor-pointer items-center gap-2"
              >
                <div
                  className="h-[10px] w-[10px] rounded-full"
                  style={{
                    backgroundColor:
                      item.color,
                  }}
                />

                <span className="text-[11px]">
                  {item.name}
                </span>
              </div>
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export default TimeAllocationChart;